import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";

import {
  articleCatalogQueryKey,
  articleCreditsQueryKey,
  articleIdentityQueryKey,
  articlePriceHistoryQueryKey,
  articleQueryKey,
  articleSetsQueryKey,
  articleSourceCoverageQueryKey,
  articleWholesaleQueryKey,
  fetchArticle,
  fetchArticleCatalog,
  fetchCompleteArticleCredits,
  fetchArticleDeliveryBreakdown,
  fetchArticleIdentity,
  fetchArticleQtyMonthly,
  fetchArticleSets,
  fetchArticleSourceCoverage,
  fetchCompleteArticleWholesale,
  fetchCompletePriceHistory,
} from "../../api/articles";
import { fetchProjects } from "../../api/projects";
import type { ProjectSummary } from "../../api/projects";

import { wholesaleEvidenceToHistoryRow } from "./history";

export function useArticleDetailData(key: string) {
  const [wholesaleSupplier, setWholesaleSupplier] = useState<string | null>(null);

  const articleQuery = useQuery({
    queryKey: articleQueryKey(key),
    queryFn: () => fetchArticle(key),
    enabled: key.length > 0,
  });

  const catalogQuery = useQuery({
    queryKey: articleCatalogQueryKey(key),
    queryFn: () => fetchArticleCatalog(key),
    enabled: key.length > 0,
    staleTime: 300_000,
  });

  const historyQuery = useQuery({
    queryKey: [...articlePriceHistoryQueryKey(key), "complete"],
    queryFn: () => fetchCompletePriceHistory(key),
    enabled: key.length > 0,
  });

  const wholesaleQuery = useQuery({
    queryKey: articleWholesaleQueryKey(key, wholesaleSupplier),
    queryFn: () => fetchCompleteArticleWholesale(key, wholesaleSupplier),
    enabled: key.length > 0,
    staleTime: 60_000,
  });

  // Gutschrift lines for the orders/evidence list only. Deliberately not merged
  // into historyQuery so the hero price KPIs and the price chart stay
  // rechnung-only.
  const creditsQuery = useQuery({
    queryKey: articleCreditsQueryKey(key),
    queryFn: () => fetchCompleteArticleCredits(key),
    enabled: key.length > 0,
    staleTime: 60_000,
  });

  const identityQuery = useQuery({
    queryKey: articleIdentityQueryKey(key),
    queryFn: () => fetchArticleIdentity(key),
    enabled: key.length > 0,
    staleTime: 300_000,
  });

  const setsQuery = useQuery({
    queryKey: articleSetsQueryKey(key),
    queryFn: () => fetchArticleSets(key),
    enabled: key.length > 0,
    staleTime: 300_000,
  });

  const sourceCoverageQuery = useQuery({
    queryKey: articleSourceCoverageQueryKey(key),
    queryFn: () => fetchArticleSourceCoverage(key),
    enabled: key.length > 0,
    staleTime: 120_000,
  });

  const qtyMonthlyQuery = useQuery({
    queryKey: ["article", key, "qty-monthly", 18],
    queryFn: () => fetchArticleQtyMonthly(key, 18),
    enabled: key.length > 0,
    staleTime: 60_000,
  });

  const deliveryQuery = useQuery({
    queryKey: ["article", key, "delivery-breakdown"],
    queryFn: () => fetchArticleDeliveryBreakdown(key),
    enabled: key.length > 0,
    staleTime: 60_000,
  });

  const projectsQuery = useQuery({
    queryKey: ["projects", "linkable", "all"],
    queryFn: () => fetchProjects({ activeOnly: false, limit: 1000 }),
    staleTime: 300_000,
  });

  const historyWithWholesale = useMemo(() => {
    const pdfRows = historyQuery.data?.items ?? [];
    const wholesaleRows = (wholesaleQuery.data?.evidence ?? []).map(
      wholesaleEvidenceToHistoryRow,
    );
    const creditRows = creditsQuery.data?.items ?? [];
    return [...pdfRows, ...wholesaleRows, ...creditRows];
  }, [
    historyQuery.data?.items,
    wholesaleQuery.data?.evidence,
    creditsQuery.data?.items,
  ]);

  const projectByKey = useMemo<ReadonlyMap<string, ProjectSummary> | undefined>(
    () =>
      projectsQuery.data
        ? new Map(
            projectsQuery.data.items.map((project) => [
              project.project_key,
              project,
            ]),
          )
        : undefined,
    [projectsQuery.data],
  );

  const firstOrderDate = useMemo(() => {
    const items = historyQuery.data?.items ?? [];
    let min: string | null = null;
    for (const r of items) {
      if (r.doc_date && (!min || r.doc_date < min)) min = r.doc_date;
    }
    return min;
  }, [historyQuery.data?.items]);

  useEffect(() => {
    if (!wholesaleSupplier) return;
    const suppliers = wholesaleQuery.data?.available_suppliers ?? [];
    if (suppliers.length > 0 && !suppliers.includes(wholesaleSupplier)) {
      setWholesaleSupplier(null);
    }
  }, [wholesaleSupplier, wholesaleQuery.data?.available_suppliers]);

  return {
    articleQuery,
    catalogQuery,
    historyQuery,
    wholesaleQuery,
    creditsQuery,
    identityQuery,
    setsQuery,
    sourceCoverageQuery,
    qtyMonthlyQuery,
    deliveryQuery,
    projectsQuery,
    wholesaleSupplier,
    setWholesaleSupplier,
    historyWithWholesale,
    projectByKey,
    firstOrderDate,
  };
}
